import {
  useCallback,
  useMemo,
  useRef,
  type MutableRefObject,
} from "react";
import type { PollTimestamps } from "@/types/frontend-types";

export interface SessionTimestampControls {
  timestamps: MutableRefObject<PollTimestamps>;
  resetTimestamps: () => void;
  updateProductsSinceTimestamp: (productsSince: string) => void;
  updateActivitySinceTimestamp: (activitySince: string) => void;
  updateProductCollabsSinceTimestamp: (input: {
    productId: string;
    collabsSince: string;
  }) => void;
  updateProductReadsSinceTimestamp: (productReadsSince: string) => void;
}

export function createEmptyPollTimestamps(): PollTimestamps {
  return {
    productsSince: null,
    chatSince: null,
    activitySince: null,
    productReadsSince: null,
    collabsSinceByProduct: {},
  };
}

export function useSessionTimestamps(): SessionTimestampControls {
  const timestamps = useRef<PollTimestamps>(createEmptyPollTimestamps());

  const resetTimestamps = useCallback((): void => {
    timestamps.current = createEmptyPollTimestamps();
  }, []);

  const updateProductsSinceTimestamp = useCallback(
    (productsSince: string): void => {
      timestamps.current.productsSince = productsSince;
    },
    [],
  );

  const updateActivitySinceTimestamp = useCallback(
    (activitySince: string): void => {
      timestamps.current.activitySince = activitySince;
    },
    [],
  );

  const updateProductCollabsSinceTimestamp = useCallback(
    (input: { productId: string; collabsSince: string }): void => {
      timestamps.current.collabsSinceByProduct[input.productId] =
        input.collabsSince;
    },
    [],
  );

  const updateProductReadsSinceTimestamp = useCallback(
    (productReadsSince: string): void => {
      timestamps.current.productReadsSince = productReadsSince;
    },
    [],
  );

  return useMemo(
    () => ({
      timestamps,
      resetTimestamps,
      updateProductsSinceTimestamp,
      updateActivitySinceTimestamp,
      updateProductCollabsSinceTimestamp,
      updateProductReadsSinceTimestamp,
    }),
    [
      resetTimestamps,
      updateProductsSinceTimestamp,
      updateActivitySinceTimestamp,
      updateProductCollabsSinceTimestamp,
      updateProductReadsSinceTimestamp,
    ],
  );
}
